import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Stack,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { Add, ArrowBack, ArrowForward } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar, PickersDay, PickersDayProps } from '@mui/x-date-pickers';
import { useFestivos, Festivo } from '../../../hooks/useFestivos';

type SnackState = {
  open: boolean;
  msg: string;
  error?: boolean;
};

type FestivoDayProps = PickersDayProps<Dayjs> & {
  isFestivo?: boolean;
};

function FestivoDay({ isFestivo, ...rest }: FestivoDayProps) {
  return (
    <PickersDay
      {...rest}
      sx={(theme) => ({
        ...(isFestivo && {
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          fontWeight: 'bold',
          '&:hover': {
            bgcolor: theme.palette.primary.dark,
          },
        }),
      })}
    />
  );
}

export default function FestivosManager() {
  const { t, i18n } = useTranslation<'translation'>();
  const currentYear = dayjs().year();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFestivo, setEditingFestivo] = useState<Festivo | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [snack, setSnack] = useState<SnackState>({ open: false, msg: '' });
  const [formData, setFormData] = useState({ fecha: '', nota: '' });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const { rows, loading, create, update, remove } = useFestivos(selectedYear);

  // Crear Map de festivos para búsqueda rápida
  const festivosMap = useMemo(() => {
    const map = new Map<string, Festivo>();
    rows.forEach((f) => map.set(f.fecha, f));
    return map;
  }, [rows]);

  // Generar meses del año seleccionado
  const months = useMemo(
    () => Array.from({ length: 12 }, (_, i) => dayjs(`${selectedYear}-01-01`).add(i, 'month')),
    [selectedYear],
  );

  const minDate = dayjs(`${selectedYear}-01-01`);
  const maxDate = dayjs(`${selectedYear}-12-31`);

  const handleOpenDialog = (fecha?: string) => {
    if (fecha && festivosMap.has(fecha)) {
      const festivo = festivosMap.get(fecha)!;
      setEditingFestivo(festivo);
      setFormData({ fecha: festivo.fecha, nota: festivo.nota ?? '' });
    } else {
      setEditingFestivo(null);
      setFormData({ fecha: fecha || '', nota: '' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingFestivo(null);
    setFormData({ fecha: '', nota: '' });
  };

  const handleSave = async () => {
    if (!formData.fecha) {
      setSnack({
        open: true,
        msg: t('holidays.errors.dateRequired', { defaultValue: 'La fecha es requerida' }),
        error: true,
      });
      return;
    }

    try {
      if (editingFestivo) {
        await update.mutateAsync({
          fecha: editingFestivo.fecha,
          nota: formData.nota || undefined,
        });
        setSnack({
          open: true,
          msg: t('holidays.success.updated', { defaultValue: 'Festivo actualizado correctamente' }),
        });
      } else {
        await create.mutateAsync({
          fecha: formData.fecha,
          nota: formData.nota || undefined,
        });
        setSnack({
          open: true,
          msg: t('holidays.success.created', { defaultValue: 'Festivo creado correctamente' }),
        });
      }
      handleCloseDialog();
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        t('holidays.errors.saveError', { defaultValue: 'Error al guardar el festivo' });
      setSnack({ open: true, msg: errorMsg, error: true });
    }
  };

  const handleDelete = async (fecha: string) => {
    try {
      await remove.mutateAsync(fecha);
      setSnack({
        open: true,
        msg: t('holidays.success.deleted', { defaultValue: 'Festivo eliminado correctamente' }),
      });
      setDeleteConfirm(null);
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        t('holidays.errors.deleteError', { defaultValue: 'Error al eliminar el festivo' });
      setSnack({ open: true, msg: errorMsg, error: true });
      setDeleteConfirm(null);
    }
  };

  const handleDayClick = (day: Dayjs, event: React.MouseEvent<HTMLElement>) => {
    const fecha = day.format('YYYY-MM-DD');
    setSelectedDay(fecha);
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedDay(null);
  };

  const handleMenuEdit = () => {
    if (selectedDay) {
      handleOpenDialog(selectedDay);
    }
    handleMenuClose();
  };

  const handleMenuDelete = () => {
    if (selectedDay) {
      setDeleteConfirm(selectedDay);
    }
    handleMenuClose();
  };

  const handleMenuCreate = () => {
    if (selectedDay) {
      handleOpenDialog(selectedDay);
    }
    handleMenuClose();
  };

  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={i18n.language.startsWith('en') ? 'en' : 'es'}
    >
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography variant="h5">
            {t('holidays.title', { defaultValue: 'Gestión de festivos' })}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton size="small" onClick={() => setSelectedYear((y) => y - 1)}>
                <ArrowBack fontSize="small" />
              </IconButton>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value as number)}
                  sx={{ '& .MuiSelect-select': { py: 1 } }}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton size="small" onClick={() => setSelectedYear((y) => y + 1)}>
                <ArrowForward fontSize="small" />
              </IconButton>
            </Stack>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              {t('holidays.add', { defaultValue: 'Añadir festivo' })}
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack direction="row" flexWrap="wrap" columnGap={4} rowGap={6}>
            {months.map((month) => {
              const monthKey = month.format('YYYY-MM-DD');
              return (
                <Box key={monthKey} sx={{ width: 300 }}>
                  <Typography
                    variant="subtitle1"
                    align="center"
                    sx={{ mb: 1, textTransform: 'capitalize' }}
                  >
                    {month.locale(i18n.language).format('MMMM')}
                  </Typography>
                  <DateCalendar
                    value={null}
                    referenceDate={month}
                    views={['day']}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={() => {}}
                    slots={{ day: FestivoDay, calendarHeader: () => null }}
                    slotProps={{
                      day: ({ day }) => {
                        const fecha = day.format('YYYY-MM-DD');
                        const festivo = festivosMap.get(fecha);
                        return {
                          isFestivo: !!festivo,
                          onClick: (e: React.MouseEvent<HTMLElement>) => {
                            e.stopPropagation();
                            handleDayClick(day, e);
                          },
                        };
                      },
                    }}
                    sx={{
                      '.MuiPickersCalendarHeader-root .MuiPickersArrowSwitcher-root': {
                        display: 'none',
                      },
                      '.MuiPickersCalendarHeader-root': {
                        justifyContent: 'center',
                      },
                      '.MuiDayCalendar-header': {
                        justifyContent: 'center',
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}

        {/* Dialog para crear/editar festivo */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingFestivo
              ? t('holidays.edit.title', { defaultValue: 'Editar festivo' })
              : t('holidays.create.title', { defaultValue: 'Nuevo festivo' })}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label={t('holidays.form.date', { defaultValue: 'Fecha' })}
                type="date"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                InputLabelProps={{ shrink: true }}
                disabled={!!editingFestivo}
                fullWidth
                required
              />
              <TextField
                label={t('holidays.form.note', { defaultValue: 'Nota' })}
                value={formData.nota}
                onChange={(e) => setFormData({ ...formData, nota: e.target.value })}
                placeholder={
                  t('holidays.form.notePlaceholder', {
                    defaultValue: 'Ej: Navidad, Día de la Constitución...',
                  }) ?? 'Ej: Navidad, Día de la Constitución...'
                }
                fullWidth
                multiline
                rows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              {t('common.cancel', { defaultValue: 'Cancelar' })}
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={create.isPending || update.isPending}
            >
              {create.isPending || update.isPending
                ? t('common.saving', { defaultValue: 'Guardando...' })
                : t('common.save', { defaultValue: 'Guardar' })}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmación de eliminación */}
        <Dialog
          open={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {t('holidays.delete.title', { defaultValue: 'Eliminar festivo' })}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {t('holidays.delete.confirm', {
                defaultValue: '¿Estás seguro de que deseas eliminar este festivo?',
              })}
            </Typography>
            {deleteConfirm && festivosMap.has(deleteConfirm) && (
              <Typography sx={{ mt: 1, fontWeight: 'bold' }}>
                {deleteConfirm} - {festivosMap.get(deleteConfirm)?.nota || ''}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirm(null)}>
              {t('common.cancel', { defaultValue: 'Cancelar' })}
            </Button>
            <Button
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              variant="contained"
              color="error"
              disabled={remove.isPending}
            >
              {remove.isPending
                ? t('common.deleting', { defaultValue: 'Eliminando...' })
                : t('common.delete', { defaultValue: 'Eliminar' })}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Menu contextual para días */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {selectedDay && festivosMap.has(selectedDay) ? (
            <>
              <MenuItem onClick={handleMenuEdit}>
                <ListItemIcon>
                  <Edit fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('holidays.edit.title', { defaultValue: 'Editar' })}</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleMenuDelete}>
                <ListItemIcon>
                  <Delete fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>{t('common.delete', { defaultValue: 'Eliminar' })}</ListItemText>
              </MenuItem>
            </>
          ) : (
            <MenuItem onClick={handleMenuCreate}>
              <ListItemIcon>
                <Add fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('holidays.add', { defaultValue: 'Añadir festivo' })}</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Snackbar para mensajes */}
        <Snackbar
          open={snack.open}
          autoHideDuration={6000}
          onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnack({ ...snack, open: false })}
            severity={snack.error ? 'error' : 'success'}
            sx={{ width: '100%' }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}
